import { useState, useEffect } from "react";
import { 
  NotebookPen, 
  Phone, 
  User, 
  CalendarClock, 
  Clock, 
  Sun, 
  Moon, 
  BellOff 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ficharService } from "@/services/ficharService";
import Solicitudes from "@/components/home/Solicitudes";
import Documentos from "@/components/home/Documentos";
import Fichaje from "@/components/home/Fichaje";
import Llamada from "@/components/home/Llamada";
import Perfil from "@/components/home/Perfil";
import { initPushService } from '@/services/pushService';
import { getSession } from '@/lib/session';
import { useTheme } from '@/hooks/use-theme';

// Badges vacíos al inicio; se cargan desde el WS en useEffect
const PENDING_BADGES: Record<string, number> = {};

// Detecta si corre en navegador web (no Android ni iOS nativo)
const getMobileSO = () => {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  if (/android/i.test(ua)) return "Android";
  if (/iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream) return "iOS";
  return "Web";
};
const IS_WEB = getMobileSO() === "Web";

const Home = () => {
  const [activeTab, setActiveTab] = useState("fichaje");
  const [userData, setUserData] = useState<any>(null);
  const [badges, setBadges] = useState<Record<string, number>>(PENDING_BADGES);
  const [isPushEnabled, setIsPushEnabled] = useState(true); 
  const [isRetryingPush, setIsRetryingPush] = useState(false); // Estado para feedback de reintento
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Función para inicializar/reintentar el servicio push
  // Al hacer click en la campana roja pide permisos de notificación explícitamente
  const handleInitPush = async (userId: string) => {
    if (isRetryingPush) return;

    setIsRetryingPush(true);
    try {
      // Solicitar permiso de notificaciones al sistema antes de reintentar
      try {
        // Nativo (Capacitor): usa PushNotifications
        const { PushNotifications } = await import('@capacitor/push-notifications');
        await PushNotifications.requestPermissions();
      } catch {
        // Web / fallback: usa la API nativa del navegador
        if ('Notification' in window) {
          await Notification.requestPermission();
        }
      }

      const pushSuccess = await initPushService(userId);
      setIsPushEnabled(pushSuccess);
    } catch (error) {
      console.error("Error inicializando push:", error);
      setIsPushEnabled(false);
    } finally {
      setIsRetryingPush(false);
    }
  };

  useEffect(() => {
    getSession().then(async (parsedUser) => {
      if (!parsedUser) {
        navigate("/");
        return;
      }
      setUserData(parsedUser);

      // Inicialización inicial de Push
      if (parsedUser.id) {
        handleInitPush(parsedUser.id);
      }

      // Carga de datos del empleado y badges
      ficharService.getDataEmployee(parsedUser.id)
        .then(async (response: any) => {
          const datosResponse = response.response;
          
          if (!datosResponse.app_so) {
            const currentSO = getMobileSO();
            try {
              await ficharService.updateSO({
                id: parsedUser.id,
                app_so: currentSO,
              });
              console.log(`SO actualizado a: ${currentSO}`);
            } catch (error) {
              console.error("Error al actualizar el SO:", error);
            }
          }

          if (datosResponse.hash) {
            ficharService.getPendientes(datosResponse.hash).then((res) => {
              if (res?.response) setBadges(res.response);
            }).catch(() => {});
          }
        }).catch((err) => console.error(err));
    });
  }, [navigate]);

  const tabs = [
    { id: "llamada",    label: "Llamada",     icon: Phone },
    { id: "documentos", label: "Reportes",    icon: NotebookPen },
    { id: "fichaje",    label: "Fichar",      icon: Clock, main: true },
    { id: "solicitudes",label: "Solicitudes", icon: CalendarClock },
    { id: "perfil",     label: "Mi perfil",   icon: User },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case "solicitudes": return <Solicitudes />;
      case "documentos": return <Documentos />;
      case "fichaje": return <Fichaje userData={userData} />;
      case "llamada": return <Llamada userData={userData} />;
      case "perfil": return <Perfil userData={userData} />;
      default: return <Fichaje userData={userData} />;
    }
  };

  if (!userData) return null;

  // ── Bloques reutilizables ──────────────────────────────────────────────────
  const headerEl = (
    <header className="gradient-primary text-primary-foreground shadow-lg safe-area-top shrink-0">
      <div className="container mx-auto px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-base xs:text-lg sm:text-xl font-bold truncate leading-tight">
              {userData.firstname} {userData.lastname}
            </h1>
            <p className="text-xs xs:text-sm opacity-90 truncate">{userData.turn_name || "FicharBot"}</p>
          </div>

          <div className="flex items-center gap-1.5 xs:gap-2 shrink-0">
            {/* Campana roja: solo en nativo (Android/iOS), no en web */}
            {!IS_WEB && !isPushEnabled && (
              <button
                onClick={() => handleInitPush(userData.id)}
                disabled={isRetryingPush}
                className={`flex items-center justify-center w-8 h-8 rounded-full bg-red-500/20 text-red-200 transition-transform active:scale-90 ${isRetryingPush ? 'animate-spin' : 'animate-pulse'}`}
                title="Notificaciones desactivadas. Toca para activarlas."
              >
                <BellOff className="w-4 h-4" />
              </button>
            )}
            <p className="font-medium text-xs xs:text-sm max-w-[70px] xs:max-w-none truncate">{userData.cifnif}</p>
            <button
              onClick={toggleTheme}
              className="p-1.5 xs:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors shrink-0"
              aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
            >
              {theme === 'light' ? (
                <Moon className="w-4 h-4 text-primary-foreground" />
              ) : (
                <Sun className="w-4 h-4 text-primary-foreground" />
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );

  const navEl = (isFixed: boolean) => (
    <nav className={`bg-card border-t border-border shadow-2xl safe-area-bottom ${isFixed ? "fixed bottom-0 left-0 right-0" : "shrink-0"}`}>
      <div className="container mx-auto px-1 py-1.5">
        <div className="flex items-end justify-around">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const isMain = tab.main;
            const count = badges[tab.id] ?? 0;

            if (isMain) {
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative -mt-6 xs:-mt-8 flex flex-col items-center justify-center transition-all ${isActive ? "scale-110" : "scale-100"}`}
                >
                  <div className={`w-13 h-13 xs:w-14 xs:h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl flex items-center justify-center transition-all ${isActive ? "gradient-secondary scale-110" : "bg-primary hover:scale-105"}`}
                    style={{ width: "clamp(48px, 14vw, 64px)", height: "clamp(48px, 14vw, 64px)" }}>
                    <Icon className="w-5 h-5 xs:w-6 xs:h-6 sm:w-7 sm:h-7 text-primary-foreground" />
                  </div>
                  <span className="text-[0.55rem] xs:text-[0.6rem] mt-1.5">{tab.label}</span>
                </button>
              );
            }

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1.5 xs:px-2 sm:px-3 rounded-lg transition-all ${isActive ? "text-primary scale-105" : "text-muted-foreground hover:text-foreground hover:scale-105"}`}
              >
                <div className="relative">
                  <Icon className={`w-5 h-5 xs:w-6 xs:h-6 mb-0.5 transition-all ${isActive ? "stroke-[2.5]" : "stroke-2"}`} />
                  {count > 0 && (
                    <span className="absolute -top-1.5 -right-2 flex items-center justify-center">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-60 animate-ping" />
                      <span className="relative flex items-center justify-center min-w-[16px] h-4 px-[3px] rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                        {count > 99 ? "99+" : count}
                      </span>
                    </span>
                  )}
                </div>
                <span className="text-[0.55rem] xs:text-[0.6rem]">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );

  // ── Layout WEB: frame de móvil centrado ───────────────────────────────────
  if (IS_WEB) {
    return (
      <div className="min-h-screen bg-slate-200 dark:bg-slate-700 flex justify-center">
        <div className="w-full max-w-[430px] min-h-screen bg-background flex flex-col shadow-[0_0_40px_rgba(0,0,0,0.25)] border-x border-slate-300 dark:border-slate-600">
          {headerEl}
          <main className="flex-1 overflow-y-auto">
            <div className="container mx-auto px-3 py-4">
              {renderContent()}
            </div>
          </main>
          {navEl(false)}
        </div>
      </div>
    );
  }

  // ── Layout NATIVO: igual que antes ────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {headerEl}
      <main className="flex-1 overflow-y-auto pb-24">
        <div className="container mx-auto px-3 py-4">
          {renderContent()}
        </div>
      </main>
      {navEl(true)}
    </div>
  );
};

export default Home;